import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { compactSymbol, formatPrice, formatPips, formatChange, getPairFlags, midpointPrice, normalizeSymbol, toFinitePrice } from '@/lib/forexUtils';
import type { ForexPair } from '@/lib/forexUtils';
import { useAuth } from '@/contexts/AuthContext';
import { chartTimeLabels, marketQuoteStatusLabel, marketSourceLabel, marketTimeLabel } from '@/lib/marketEvidence';
import { useMarketAccount } from '@/contexts/useMarketAccount';
import MarketAccountPicker from '@/components/MarketAccountPicker';
import forexApi, { sameMarketSource, type MarketProjectionContext, type MarketTickerDTO } from '@/services/forexApi';
import type { MarketTickerTimeframe } from '@/services/forexApi';

// Timeframe labels for chart X-axis
function getTimeframeLabels(tf: MarketTickerTimeframe, timestamps: number[]): string[] {
  return chartTimeLabels(timestamps, tf);
}

// Simple SVG sparkline chart
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (!data.length) return null;
  const width = 60;
  const height = 24;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

// Detailed chart for selected pair with Y-axis labels
function PairChart({
  data,
  timestamps,
  height = 140,
  timeFrame = '1H',
}: {
  data: number[];
  timestamps: number[];
  height?: number;
  timeFrame?: MarketTickerTimeframe;
}) {
  if (!data.length) return null;
  const width = 320;
  const padding = { top: 10, right: 56, bottom: 24, left: 0 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = padding.left + (i / (data.length - 1)) * chartW;
      const y = padding.top + chartH - ((v - min) / range) * chartH;
      return `${x},${y}`;
    })
    .join(' ');

  // Area fill path
  const areaPath = `M${padding.left},${padding.top + chartH} L${points.split(' ')[0]} L${points.replace(/ /g, ' L')} L${padding.left + chartW},${padding.top + chartH} Z`;

  // X-axis labels (time) — driven by timeframe
  const timeLabels = getTimeframeLabels(timeFrame, timestamps);

  // Y-axis labels (price) - 4 ticks on right
  const yTicks = 4;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = max - (range * i) / yTicks;
    return v.toFixed(5);
  });

  const lineColor = '#6366F1';
  const currentPrice = data[data.length - 1];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Area fill */}
      <path d={areaPath} fill="url(#areaGrad)" />

      {/* Line */}
      <polyline
        fill="none"
        stroke={lineColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />

      {/* Current price dot */}
      {(() => {
        const lastX = padding.left + chartW;
        const lastY = padding.top + chartH - ((currentPrice - min) / range) * chartH;
        return (
          <>
            <circle cx={lastX} cy={lastY} r="4" fill="#6366F1" />
            <circle cx={lastX} cy={lastY} r="8" fill="#6366F1" opacity="0.15" />
          </>
        );
      })()}

      {/* Current price badge on right */}
      {(() => {
        const ly = padding.top + chartH - ((currentPrice - min) / range) * chartH;
        return (
          <g>
            <rect
              x={width - 54}
              y={ly - 10}
              width="50"
              height="20"
              rx="4"
              fill="#6366F1"
            />
            <text
              x={width - 29}
              y={ly + 4}
              textAnchor="middle"
              fill="white"
              fontSize="10"
              fontWeight="600"
            >
              {currentPrice.toFixed(5)}
            </text>
          </g>
        );
      })()}

      {/* Y-axis labels on right */}
      {yLabels.map((label, i) => {
        const y = padding.top + (chartH * i) / yTicks;
        return (
          <text
            key={`y-${i}`}
            x={width - 4}
            y={y + 3}
            textAnchor="end"
            fill="#94A3B8"
            fontSize="9"
          >
            {label}
          </text>
        );
      })}

      {/* X-axis time labels */}
      {timeLabels.map((label, i) => {
        const x = padding.left + (i / (timeLabels.length - 1)) * chartW;
        return (
          <text
            key={i}
            x={x}
            y={height - 4}
            textAnchor="middle"
            fill="#94A3B8"
            fontSize="9"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

// Only show the server's top-of-book bid/ask; no order-book volume is available.
function TopOfBook({ pair, bid, ask, digits, spread, spreadUnit }: {
  pair: string; bid: number | null; ask: number | null; digits?: number;
  spread: number; spreadUnit?: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div><span className="text-text-secondary">买价</span><div className="font-semibold text-[#10B981]">{formatPrice(pair, bid, digits)}</div></div>
      <div><span className="text-text-secondary">卖价</span><div className="font-semibold text-[#F43F5E]">{formatPrice(pair, ask, digits)}</div></div>
      <div className="col-span-2 text-text-secondary">点差 {spread} {spreadUnit || 'point'} · 服务端报价，无订单簿手数</div>
    </div>
  );
}

export default function MarketWatchlist() {
  const { scopeKey } = useMarketAccount();
  return <MarketWatchlistContent key={scopeKey} />;
}

function MarketWatchlistContent() {
  const { scope } = useMarketAccount();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [pairs, setPairs] = useState<(ForexPair & { bid: number | null; ask: number | null; sourceId?: string; marketContext?: MarketProjectionContext | null; quoteStatus?: string; quoteTime?: number | null; agentCount: number; history: number[]; historyTimes: number[]; historyLoading?: boolean; historyError?: boolean })[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [timeFrame, setTimeFrame] = useState<MarketTickerTimeframe>('1H');
  const [showDepth, setShowDepth] = useState(false);

  const loadTickers = useCallback((signal: AbortSignal, initial = false) => {
    if (initial) setLoading(true);
    return forexApi.listMarketTickers(accessToken, 'symbol_asc', scope, signal)
      .then((tickers) => {
        if (signal.aborted) return;
        setPairs((previous) => {
          const previousByPair = new Map(previous.map((item) => [item.pair, item]));
          return tickers.map((ticker: MarketTickerDTO, index) => {
            const pair = normalizeSymbol(ticker.symbol);
            const previousItem = previousByPair.get(pair);
            const previousSource = sameMarketSource(previousItem?.marketContext, ticker.market_context) ? previousItem : undefined;
            return {
              pair,
              price: midpointPrice(ticker.bid, ticker.ask),
              bid: toFinitePrice(ticker.bid),
              ask: toFinitePrice(ticker.ask),
              sourceId: ticker.source_id,
              marketContext: ticker.market_context,
              quoteStatus: ticker.freshness_status || ticker.quote_status,
              quoteTime: ticker.quote_time,
              agentCount: ticker.agent_count ?? 0,
              priceDigits: ticker.digits ?? undefined,
              change: ticker.change_pct,
              pips: ticker.change_pips,
              changeUnitLabel: ticker.change_unit_label ?? 'point',
              spread: toFinitePrice(ticker.spread_units) ?? ticker.spread_points,
              spreadUnitLabel: ticker.spread_unit_label ?? 'point',
              flags: getPairFlags(ticker.symbol),
              favorite: previousItem?.favorite ?? index === 0,
              history: previousSource?.history ?? [],
              historyTimes: previousSource?.historyTimes ?? [],
              historyLoading: previousSource?.historyLoading ?? false,
              historyError: previousSource?.historyError ?? false,
            };
          });
        });
        setSelectedIndex((index) => (tickers.length === 0 ? 0 : Math.min(index, tickers.length - 1)));
        setLoadError('');
      })
      .catch(() => {
        if (!signal.aborted) {
          setPairs([]);
          setSelectedIndex(0);
          setLoadError('行情数据暂不可用');
        }
      })
      .finally(() => {
        if (initial && !signal.aborted) setLoading(false);
      });
  }, [accessToken, scope]);

  useEffect(() => {
    const controller = new AbortController();
    let busy = false;
    const poll = async (initial = false) => {
      if (busy || controller.signal.aborted) return;
      busy = true;
      try { await loadTickers(controller.signal, initial); } finally { busy = false; }
    };
    void poll(true);
    const timer = window.setInterval(() => { void poll(); }, 8000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [loadTickers]);

  const selectedPair = pairs[selectedIndex];
  const selectedPairName = selectedPair?.pair;
  const sourceKey = JSON.stringify(selectedPair?.marketContext ?? null);
  useEffect(() => {
    const source = JSON.parse(sourceKey) as MarketProjectionContext | null;
    if (!selectedPairName || !scope || !source) return;
    const controller = new AbortController();
    const matches = (p: typeof pairs[number]) => p.pair === selectedPairName && sameMarketSource(p.marketContext, source);
    setPairs(prev => prev.map(p => matches(p) ? { ...p, history: [], historyTimes: [], historyLoading: true, historyError: false } : p));
    forexApi.getMarketTickerHistory(compactSymbol(selectedPairName), timeFrame, accessToken, scope, source, controller.signal)
      .then(history => {
        if (controller.signal.aborted) return;
        const points = history.items.filter(item => toFinitePrice(item.mid) != null && Number.isFinite(item.timestamp) && item.timestamp > 0);
        setPairs(prev => prev.map(p => matches(p) ? { ...p, history: points.map(point => toFinitePrice(point.mid) as number),
          historyTimes: points.map(point => point.timestamp), historyLoading: false, historyError: false } : p));
      }).catch(() => {
        if (!controller.signal.aborted) setPairs(prev => prev.map(p => matches(p)
          ? { ...p, history: [], historyTimes: [], historyLoading: false, historyError: true } : p));
      });
    return () => controller.abort();
  }, [accessToken, selectedPairName, timeFrame, sourceKey, scope]);

  const toggleFavorite = useCallback((index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPairs((prev) => prev.map((p, i) => (i === index ? { ...p, favorite: !p.favorite } : p)));
  }, []);

  return (
    <div className="px-5 mt-5">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-bar-chart-grouped-line text-brand-peri text-sm" />
          <span className="text-text-primary font-bold text-base">行情观察</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/market')}
            className="text-brand-peri text-xs font-medium flex items-center gap-0.5"
          >
            查看更多
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div className="mb-3"><MarketAccountPicker /></div>

      {loading && (
        <div className="bg-white rounded-2xl p-5 text-center" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div className="w-8 h-8 border-2 border-indigo-100 border-t-indigo-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-xs">正在同步行情...</p>
        </div>
      )}

      {!loading && pairs.length === 0 && (
        <div className="bg-white rounded-2xl p-5 text-center" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div className="w-11 h-11 mx-auto mb-3 rounded-full bg-gray-50 flex items-center justify-center">
            <i className="ri-bar-chart-line text-gray-300 text-xl" />
          </div>
          <p className="text-gray-800 font-semibold text-sm">暂无可用行情</p>
          <p className="text-gray-400 text-xs mt-1">
            {loadError || '等待服务端可确认的报价来源'}
          </p>
          <button
            onClick={() => navigate('/market')}
            className="mt-4 px-4 py-2 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium"
          >
            查看行情页
          </button>
        </div>
      )}

      {/* Horizontal scroll watchlist cards */}
      {pairs.length > 0 && (
      <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-2 -mx-5 px-5">
        {pairs.map((pair, index) => {
          const isSelected = index === selectedIndex;
          const isUp = pair.change >= 0;
          return (
            <button
              key={pair.pair}
              onClick={() => setSelectedIndex(index)}
              className="flex-shrink-0 text-left px-3 py-2.5 rounded-xl transition-all duration-200 active:scale-95"
              style={{
                width: '108px',
                background: isSelected ? '#EBF0FF' : 'white',
                border: isSelected ? '1.5px solid #6366F1' : '1px solid rgba(0,0,0,0.04)',
                boxShadow: isSelected
                  ? '0 4px 16px rgba(99,102,241,0.12)'
                  : '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-text-primary font-bold text-xs">{pair.pair}</span>
                <i
                  className={
                    pair.favorite
                      ? isSelected
                        ? 'ri-star-fill text-brand-peri'
                        : 'ri-star-fill text-warn'
                      : isSelected
                        ? 'ri-star-line text-brand-peri'
                        : 'ri-star-line text-gray-300'
                  }
                  onClick={(e) => toggleFavorite(index, e)}
                  style={{ fontSize: '12px' }}
                />
              </div>
              <div className="text-text-primary font-bold text-base mb-0.5">
                {formatPrice(pair.pair, pair.price, pair.priceDigits)}
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="text-xs font-medium"
                  style={{ color: isUp ? '#22C55E' : '#F43F5E' }}
                >
                  {formatChange(pair.change)}
                </span>
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: isUp ? '#22C55E' : '#F43F5E' }}
              >
                {formatPips(pair.pips, pair.changeUnitLabel)}
              </div>
            </button>
          );
        })}
      </div>
      )}

      {/* Selected pair detail card */}
      {selectedPair && (
        <div
          className="mt-3 bg-white rounded-2xl p-4"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          {/* Top info */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full overflow-hidden text-base"
                style={{ background: '#F3F4F6' }}
              >
                {selectedPair.flags}
              </span>
              <span className="text-text-primary font-bold text-base">
                {selectedPair.pair}
              </span>
              <i
                className={
                  selectedPair.favorite ? 'ri-star-fill text-brand-peri' : 'ri-star-line text-gray-300'
                }
                onClick={(e) => toggleFavorite(selectedIndex, e)}
                style={{ fontSize: '16px', cursor: 'pointer' }}
              />
            </div>
            <div className="flex gap-1">
              {(['1H', '4H', '1D', '1W'] as const).map((tf) => (
                <button
                  key={tf}
                  onClick={() => setTimeFrame(tf)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: timeFrame === tf ? '#EEF2FF' : 'transparent',
                    color: timeFrame === tf ? '#6366F1' : '#94A3B8',
                    border:
                      timeFrame === tf ? '1px solid #C7D2FE' : '1px solid transparent',
                  }}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>

          {/* Price row */}
          <div className="flex flex-col gap-1 mb-3">
            <span className="text-text-primary font-bold text-2xl tracking-tight">
              {formatPrice(selectedPair.pair, selectedPair.price, selectedPair.priceDigits)}
            </span>
            <div className="flex items-center gap-2">
              <span
                className="text-sm font-semibold"
                style={{
                  color: selectedPair.change >= 0 ? '#22C55E' : '#F43F5E',
                }}
              >
                {formatPips(selectedPair.pips, selectedPair.changeUnitLabel)}
              </span>
              <span
                className="text-xs"
                style={{
                  color: selectedPair.change >= 0 ? '#22C55E' : '#F43F5E',
                }}
              >
                {formatChange(selectedPair.change)}
              </span>
            </div>
          </div>

          {/* Chart */}
          <div className="mb-3">
            {selectedPair.historyLoading ? (
              <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">正在同步历史行情...</div>
            ) : selectedPair.historyError ? (
              <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">历史行情暂不可用</div>
            ) : selectedPair.history.length >= 2 ? (
              <PairChart data={selectedPair.history} timestamps={selectedPair.historyTimes} timeFrame={timeFrame} />
            ) : (
              <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">数据积累中</div>
            )}
          </div>

          {/* Depth toggle + depth section */}
          <button
            onClick={() => setShowDepth(!showDepth)}
            className="flex items-center gap-1.5 text-xs text-text-secondary mb-2"
          >
            <i className="ri-stack-line" style={{ color: showDepth ? '#6366F1' : undefined }} />
            买卖报价
            {showDepth ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
          </button>
          {showDepth && (
            <div className="mb-3 px-2 py-2 rounded-xl" style={{ background: '#F9FAFB' }}>
              <TopOfBook pair={selectedPair.pair} bid={selectedPair.bid} ask={selectedPair.ask} digits={selectedPair.priceDigits} spread={selectedPair.spread} spreadUnit={selectedPair.spreadUnitLabel} />
            </div>
          )}

          {/* Bottom info bar - 2 rows */}
          <div
            className="px-3 py-2.5 rounded-xl"
            style={{ background: '#F3F6FF' }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-brand-peri text-xs" />
                <span className="text-xs text-text-secondary">
                  {marketSourceLabel(selectedPair.sourceId)} · {marketQuoteStatusLabel(selectedPair.quoteStatus)} · {marketTimeLabel(selectedPair.quoteTime)}
                </span>
              </div>
              <span className="text-gray-300 text-xs">·</span>
              <div className="flex items-center gap-1">
                <i className="ri-exchange-dollar-line text-brand-peri text-xs" />
                <span className="text-xs text-text-secondary">
                  Spread {selectedPair.spread} {selectedPair.spreadUnitLabel ?? 'point'}
                </span>
              </div>
              <span className="text-gray-300 text-xs">·</span>
              <div className="flex items-center gap-1">
                <i className="ri-robot-2-line text-brand-peri text-xs" />
                <span className="text-xs text-text-secondary">{selectedPair.agentCount} 个 AI 智能体</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
